/**
 * Gun Game domain types. The ladder itself ships as ladder.json in this
 * package once weapons land (milestone 7/8); these types are its contract.
 */
/**
 * BURNOUT rules — race the clock up the Gun Game ladder. Clock ticks and kills
 * both promote; a three-kill life freezes clock promotions briefly. The final
 * knife must score a kill before its fuse expires. Knife deaths and fuse-outs
 * cost three tiers rather than resetting the whole run.
 *
 * STOKE — the wager button: any player below the top rung may light their OWN
 * fuse. A kill inside the window cashes `stokeBonusTiers` extra rungs on top
 * of the kill's normal promotion; letting it expire is the standard burn-out
 * (a real death plus the setback). Same fuse length as the knife fuse so one
 * clock reads the same everywhere.
 *
 * Config-driven — keep tunables here, not in code.
 */
export const BURNOUT = {
    /** Everyone alive climbs one rung this often (ms). Derived from
     *  state.matchStartedAt on the server so it costs no replicated field —
     *  GunGameState is at the Colyseus 64-field ceiling. */
    heatStepMs: 18000,
    /** Faster global clock during the final minute of timed rounds. */
    finalHeatStepMs: 10000,
    finalMinuteMs: 60000,
    /** Three kills in one life pauses clock promotions for this player. */
    streakFreezeMs: 10000,
    streakKills: 3,
    /** Grace at the final (knife) tier before you burn out (ms). */
    fuseMs: 12000,
    /** Knife death / fuse expiry setback. */
    setbackTiers: 3,
    /** STOKE: extra rungs a kill inside a self-lit fuse pays (on top of the
     *  kill's normal +1 — a cashed stoke is +3 total). */
    stokeBonusTiers: 2,
    /** STOKE: per-player re-arm delay, so a cashed wager can't instantly
     *  re-light off a double-tap. */
    stokeCooldownMs: 6000,
};
/**
 * FLAGS rules — team capture-the-flag on every map.
 *
 * Two teams own opposite spawn zones and colored flags. Steal the enemy flag
 * and return it to your own base to score. The internal id remains `plunder`
 * for room/link compatibility; players see the mode as FLAGS.
 */
export const PLUNDER = {
    /** Deliveries needed to take the round. */
    target: 3,
    /** A loose chest nobody can reach for this long returns to its home hold —
     *  same stall guard Treasure uses, and it matters more here because the sea
     *  floor is 9 m down and a sunken chest reads as "it vanished". */
    looseReturnMs: 45000,
};
/**
 * IMPERION RELAY rules — one hot-potato superweapon, always armed.
 *
 * The map's IMPERION chest is the relay: claiming it makes you the holder and
 * the Imperion is simply ARMED — no charging, the 1.2 s hold-to-fire is the
 * only rate limit. Beam kills score; an ordinary kill on the holder transfers
 * the relay to the killer; a void/suicide death returns it to the chest.
 * First to `target` beam kills wins. Everyone else plays deathmatch rules
 * (weapon chest, no ladder progression).
 *
 * (2026-07-23: the AIRTIME-banks-charges mechanic was removed by request —
 * "just grab the imperion". `maxCharges` survives because the per-shot
 * sound-pitch table and HUD pips key off it.)
 */
export const RELAY = {
    /** Beam kills to take the round. */
    target: 5,
    /** Ammo the holder is pinned at — matches IMPERION.shotsPerChest so the
     *  per-shot sound-pitch table lines up. */
    maxCharges: 3,
    /** Bot target-score attraction toward the holder (m-equivalent). Between
     *  the bounty bias (28) and the chest-carrier bias (45). */
    botTargetBias: 34,
};
/**
 * ONE GUN rules — the ladder rides the WEAPON, not the player.
 *
 * There is exactly one gun in the match: a weapon chest holds it until someone
 * claims it. Every kill the holder lands WITH the gun promotes THE GUN one
 * ladder rung (never the ladder's final melee rung — that knife is what
 * everyone else is carrying). Kill the holder and the gun drops where they
 * fall, tier preserved; a void death sends it back to its chest. The gun kill
 * landed on the last gun rung wins the match. Everyone without the gun carries
 * the ballistic knife — throwable, instakill, always lethal at close range.
 */
export const ONEGUN = {
    /** A loose dropped gun nobody reaches for this long returns to its chest —
     *  same stall guard Treasure uses (a gun knifed off a rooftop edge or into
     *  a lava pocket must not soft-lock the mode). */
    looseReturnMs: 45000,
    /** Bot target-score attraction toward the holder (m-equivalent). Above the
     *  relay bias (34) — the gun is both the objective AND the room's only
     *  ranged threat — but below the chest-carrier commit (45). */
    botTargetBias: 40,
    /** Bots inside this range break off and race a LOOSE gun (the relay uses
     *  40 m; the gun matters more — it is the only way to shoot anybody). */
    botRaceDist: 60,
    /** A bot HOLDING the gun commits to a full backpedal (kite) when its target
     *  is inside this range: in ONE GUN the crowd is unarmed, so a knifer must
     *  reach melee range to punish, and gun-kills are the only thing that moves
     *  the ladder. The generic combat spacing only eases to −0.4 inside half the
     *  ideal range, which a knifer sprinting at 8.2 m/s runs straight through —
     *  the holder needs the committed retreat to hold its structural range
     *  advantage and farm the chasers. Beyond this the chaser can't punish, so
     *  the holder closes and fights normally. See botThink's oneGunHolder kite. */
    botKiteRange: 9,
};
/** The gun's rung count for a ladder of `len` weapons: every rung except the
 *  final melee tier (which is what the unarmed crowd carries). The winning
 *  kill is a gun kill landed while the gun sits on its last rung. */
export const onegunRungs = (len) => Math.max(1, len - 1);
/** Mode rules (3.3). Config-driven — keep tunables here, not in code. */
export const GUNGAME = {
    /** Rule 4 (optional, on by default): dying to the void demotes one tier. */
    suicideDemotes: true,
    /** Post-win celebration time before the match resets. */
    matchResetDelayMs: 8000,
};
//# sourceMappingURL=gungame.js.map